/**
 * Recording validation on the discography grid (WMC-565).
 *
 *  - View mode: the Validation column (right after Evidence) is the inline
 *    ✓ ? ✕ trio, live — one click sets that row's status — with the current
 *    status lit. On hover the same pencil the mass-edit cells show enters
 *    validation mode (so does "Validate" in the actions menu).
 *  - Validation mode: a checkbox column appears, every row shows a single
 *    non-interactive icon of its current status, selected rows get the
 *    bulk-edit background, the top toolbar becomes "Validation · N selected
 *    · Done", and a floating bar carries "Select pending", the trio for the
 *    selection and Clear. Like bulk edit, the mode collapses the grid into
 *    one page holding every matching record, so the selection covers the
 *    whole result set.
 *  - Filtering by status is the Validation column's own filter, server-side
 *    like every other column's (`validation_state.status`). "Select pending"
 *    sets that filter to Pending and selects what is left.
 *  - A rejected recording is muted across the row: no field is editable,
 *    it cannot be ticked in bulk edit and has no Edit button — only its
 *    validation status can still change.
 */
import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Pencil } from 'lucide-react';
import type { IHeaderParams } from 'ag-grid-community';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { WCheckBox } from 'shared_components/components/WCheckBox/WCheckBox';
import {
  ValidationCounts,
  ValidationStatusIcon,
  ValidationTrio,
} from './ValidationTrio';
import {
  countStatuses,
  pendingRowIds,
  STATUS_ORDER,
  useValidationStore,
  validationStore,
} from './validationStore';

/* ---------------------------------------------------------------- cells */

/** The mass-edit cell pencil (hidden until the cell is hovered), pointed at
 *  validation mode instead of a field. */
const ValidationPencil: React.FC = () => (
  <button
    type="button"
    aria-label="Validate"
    title="Validate recordings"
    className="tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-flex-shrink-0 tw-ml-1 tw-p-1 tw-rounded hover:tw-bg-gray-700 tw-text-textDove hover:tw-text-white"
    onClick={(e) => {
      e.stopPropagation();
      validationStore.setMode(true);
    }}
  >
    <Pencil className="tw-w-3.5 tw-h-3.5" />
  </button>
);

/** View mode: live inline trio + hover pencil. Validation mode, or
 *  `readOnly` (bulk edit): one non-interactive icon of the current status. */
export const ValidationCell: React.FC<{
  rowId: string;
  readOnly?: boolean;
}> = ({ rowId, readOnly }) => {
  const mode = useValidationStore((s) => s.mode);
  return mode || readOnly ? (
    <div className="tw-flex tw-h-full tw-items-center tw-w-full">
      <ValidationStatusIcon rowId={rowId} />
    </div>
  ) : (
    <div className="tw-group tw-flex tw-h-full tw-items-center tw-justify-between tw-w-full">
      <ValidationTrio rowId={rowId} />
      <ValidationPencil />
    </div>
  );
};

/** Muted, locked rejected rows. Opacity and pointer-events go on the cells
 *  rather than the row so the Validation column (and the checkbox used to
 *  un-reject in validation mode, and the expand chevron) stay live. Injected
 *  once by MasterDiscography; the class comes from its `rowClassRules`. */
export const ValidationRejectedStyles: React.FC = () => (
  <style>{`
    .ag-row.validation-rejected .ag-cell:not([col-id="validation"]):not([col-id="validationSelect"]):not([col-id="expand"]) {
      opacity: 0.35;
      pointer-events: none;
      user-select: none;
    }
    .ag-row.validation-rejected .ag-cell[col-id="expand"] { opacity: 0.35; }
  `}</style>
);

export const ValidationSelectCell: React.FC<{ rowId: string }> = ({
  rowId,
}) => {
  const checked = useValidationStore((s) => !!s.selected[rowId]);
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className="tw-flex tw-h-full tw-items-center tw-justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <WCheckBox
        checkboxSize="sm"
        checked={checked}
        onChange={() => validationStore.toggleSelected(rowId)}
      />
    </div>
  );
};

/** Header checkbox: select all / none of the page's rows, which come in
 *  through `headerComponentParams`. */
export const ValidationSelectAllHeader: React.FC<
  IHeaderParams & { getPageRowIds: () => string[] }
> = ({ getPageRowIds }) => {
  const selected = useValidationStore((s) => s.selected);
  const ids = getPageRowIds();
  const allSelected = ids.length > 0 && ids.every((id) => selected[id]);
  return (
    <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
      <WCheckBox
        checkboxSize="sm"
        checked={allSelected}
        onChange={() =>
          allSelected
            ? validationStore.clearSelected()
            : validationStore.setSelected(ids)
        }
      />
    </div>
  );
};

/* ------------------------------------------------------- mode toolbar */

/** Replaces the search/CTA row while validation mode is on — same shape as
 *  the bulk-edit and mass-edit takeovers. */
export const ValidationToolbar: React.FC<{ rowIds: string[] }> = ({
  rowIds,
}) => {
  const statuses = useValidationStore((s) => s.statuses);
  const selected = useValidationStore((s) => s.selected);
  const counts = useMemo(
    () => countStatuses(statuses, rowIds),
    [statuses, rowIds]
  );
  const n = Object.keys(selected).length;
  return (
    <>
      <div className="tw-flex tw-items-center tw-gap-4">
        <span className="tw-text-white tw-text-base tw-font-semibold">
          Validation
        </span>
        <span className="tw-text-sm tw-text-textDove">{n} selected</span>
        <span className="tw-text-sm tw-text-textDove">·</span>
        <ValidationCounts counts={counts} order={STATUS_ORDER} />
      </div>
      <WButton
        variant="secondary"
        label="Done"
        onClick={() => validationStore.setMode(false)}
      />
    </>
  );
};

/* ----------------------------------------------------------- floating bar */

/** Shown for the whole of validation mode, portalled to body so it floats
 *  above the grid. It keeps one height whatever its text says. */
export const ValidationBar: React.FC<{
  rowIds: string[];
  onSelectPending: () => void;
}> = ({ rowIds, onSelectPending }) => {
  const selected = useValidationStore((s) => s.selected);
  const statuses = useValidationStore((s) => s.statuses);
  const selectedIds = useMemo(() => Object.keys(selected), [selected]);
  const pendingCount = useMemo(
    () => pendingRowIds(statuses, rowIds).length,
    [rowIds, statuses]
  );
  const none = selectedIds.length === 0;
  const headline = none ? 'Nothing selected' : `${selectedIds.length} selected`;

  return createPortal(
    <div
      role="toolbar"
      aria-label="Validation tools"
      className="tw-fixed tw-bottom-6 tw-left-1/2 -tw-translate-x-1/2 tw-z-[9000] tw-flex tw-items-center tw-gap-4 tw-rounded-2xl tw-bg-surfaceBlack90 tw-border tw-border-gray-600 tw-px-5 tw-py-3 tw-text-white tw-shadow-[0_8px_30px_rgba(0,0,0,0.6)]"
    >
      <WButton
        variant="secondary"
        label={`Select pending${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
        onClick={onSelectPending}
      />
      <div className="tw-w-px tw-h-8 tw-bg-gray-600" />
      {/* Fixed box: two one-line slots, so the bar never changes height or
          width when the headline or the helper text changes. */}
      <div className="tw-flex tw-h-9 tw-w-[300px] tw-flex-col tw-justify-center tw-gap-0.5">
        <span
          className="tw-block tw-h-4 tw-truncate tw-text-sm tw-font-semibold tw-leading-4"
          title={headline}
        >
          {headline}
        </span>
        <span className="tw-block tw-h-4 tw-truncate tw-text-[11px] tw-leading-4 tw-text-textDove">
          {none
            ? 'tick rows, then approve, review or reject them here'
            : 'approve, review or reject the selection'}
        </span>
      </div>
      <ValidationTrio rowIds={selectedIds} size="lg" disabled={none} />
      <div className="tw-w-px tw-h-8 tw-bg-gray-600" />
      <WButton
        variant="secondary"
        label="Clear"
        disabled={none}
        onClick={() => validationStore.clearSelected()}
      />
    </div>,
    document.body
  );
};
